import { current, original, produce } from "immer";
import { useMemo, useReducer } from "react";
import type { GroupedQuoteLines } from "~/features/orders/common/components/v2/QuoteTable/types";
import type { QuoteDetailsMO } from "~/features/orders/common/components/v2/models";
import { groupByMainQuoteLine } from "~/features/orders/common/components/v2/QuoteTable/utils";

export interface QuoteSelectionStateProps {
  /* Refers to main quote lines */
  isAllSelected: boolean;
  /* Refers to main quote lines */
  isSomeSelected: boolean;
  /* Refers to any quote lines */
  isAnythingSelected: boolean;
  isAllDisabled: boolean;
  selectedLineNos: Record<string, boolean>;
  groupedQuoteLines: GroupedQuoteLines[];
  // Actions
  toggleAllLines: () => void;
  toggleLine: (lineNo: string) => void;
  init: (quoteDetails: QuoteDetailsMO) => void;
}

function init(quoteDetails: QuoteDetailsMO, preselect = true) {
  return {
    type: "INIT",
    payload: { quoteDetails, preselect },
  } as const;
}

function toggleLine(lineNo: string) {
  return {
    type: "TOGGLE_LINE",
    payload: { lineNo },
  } as const;
}

function toggleAllLines() {
  return {
    type: "TOGGLE_ALL_LINES",
  } as const;
}

type SelectionStateActions =
  | ReturnType<typeof toggleLine>
  | ReturnType<typeof toggleAllLines>
  | ReturnType<typeof init>;

export interface QuoteDetailsSelectionState {
  /* Refers to main quote lines */
  isAllSelected: boolean;
  /* Refers to main quote lines */
  isSomeSelected: boolean;
  /* Refers to any quote lines */
  isAnythingSelected: boolean;
  isAllDisabled: boolean;
  selectedLineNos: Record<string, boolean>;
  groupedQuoteLines: GroupedQuoteLines[];
  lineNoToAllLineNoInGroup: Record<string, string[]>;
}

const QUOTE_DETAILS_SELECTION_INITIAL_STATE: QuoteDetailsSelectionState = {
  isSomeSelected: false,
  isAllSelected: false,
  isAnythingSelected: false,
  isAllDisabled: false,
  selectedLineNos: {},
  groupedQuoteLines: [],
  lineNoToAllLineNoInGroup: {},
};

export function quoteDetailsSelectionStateReducer(state: QuoteDetailsSelectionState, action: SelectionStateActions) {
  return produce(state, (draft) => {
    function updateDerivatives() {
      const raw = current(draft);
      draft.isAllSelected = raw.groupedQuoteLines.every((group) => {
        const lines = [group.mainQuoteLine];
        return lines.some((line) => raw.selectedLineNos[line.lineNo]);
      });
      draft.isSomeSelected = raw.groupedQuoteLines.some((group) => {
        const lines = [group.mainQuoteLine];
        return lines.some((line) => raw.selectedLineNos[line.lineNo]);
      });
      draft.isAnythingSelected = Object.values(raw.selectedLineNos).filter(Boolean).length > 0;
    }

    switch (action.type) {
      case "INIT": {
        const { quoteDetails, preselect } = action.payload;
        const groupedQuoteLines = groupByMainQuoteLine(quoteDetails.quoteLines);
        const { lineNoToAllLineNoInGroup } = prepareMapping(groupedQuoteLines);
        draft.groupedQuoteLines = groupedQuoteLines;
        draft.lineNoToAllLineNoInGroup = lineNoToAllLineNoInGroup;

        for (const group of groupedQuoteLines) {
          draft.selectedLineNos[group.mainQuoteLine.lineNo] = preselect && !group.mainQuoteLine.disabled;
          for (const line of group.alternativeQuoteLines) {
            draft.selectedLineNos[line.lineNo] = false;
          }
        }
        draft.isAllDisabled = calculateIsAllDisabled(groupedQuoteLines);
        updateDerivatives();
        break;
      }

      case "TOGGLE_LINE": {
        const { lineNo } = action.payload;
        const isSelected = draft.selectedLineNos[lineNo];
        if (isSelected) {
          // Easy, just deselect this one
          delete draft.selectedLineNos[lineNo];
        } else {
          // Also easy! Just select this one. Because when selecting manually, we can have multiple lines selected in one group
          draft.selectedLineNos[lineNo] = true;
        }
        updateDerivatives();
        break;
      }
      case "TOGGLE_ALL_LINES": {
        const isAllSelected = calculateIsAllSelected(draft.groupedQuoteLines, draft.selectedLineNos);
        if (isAllSelected) {
          // Easy, just clear everything
          draft.selectedLineNos = {};
        } else {
          // In each group that does not have anything selected, select the main line
          for (const group of draft.groupedQuoteLines) {
            const originalObj = original(draft.selectedLineNos)!;
            const mainLineNo = group.mainQuoteLine.lineNo;
            const isSomethingSelected = draft.lineNoToAllLineNoInGroup[mainLineNo].some(
              (lineNo) => originalObj[lineNo],
            );
            if (isSomethingSelected) {
              continue;
            }
            draft.selectedLineNos[mainLineNo] = !group.mainQuoteLine.disabled;
          }
        }
        updateDerivatives();
        break;
      }
    }
  });
}

quoteDetailsSelectionStateReducer.actions = {
  toggleLine,
  toggleAllLines,
  init,
} as const;

quoteDetailsSelectionStateReducer.initialState = QUOTE_DETAILS_SELECTION_INITIAL_STATE;

export function useQuoteDetailsSelectionState(): QuoteSelectionStateProps {
  const [state, dispatch] = useReducer(quoteDetailsSelectionStateReducer, QUOTE_DETAILS_SELECTION_INITIAL_STATE);

  const actions = useMemo(
    () => ({
      init: (quoteDetails: QuoteDetailsMO) => dispatch(init(quoteDetails)),
      toggleLine: (lineNo: string) => dispatch(toggleLine(lineNo)),
      toggleAllLines: () => dispatch(toggleAllLines()),
    }),
    [dispatch],
  );

  return {
    ...state,
    ...actions,
  };
}

const prepareMapping = (groupedQuoteLines: GroupedQuoteLines[]) => {
  const lineNoToAllLineNoInGroup = {} as Record<string, string[]>;
  groupedQuoteLines.forEach((group) => {
    const allLineNos = [group.mainQuoteLine.lineNo, ...group.alternativeQuoteLines.map((line) => line.lineNo)];
    allLineNos.forEach((lineNo) => {
      lineNoToAllLineNoInGroup[lineNo] = allLineNos;
    });
  });
  return { lineNoToAllLineNoInGroup };
};

/**
 * In every group either one quote line is selected or all are disabled
 */
function calculateIsAllSelected(groupedQuoteLines: GroupedQuoteLines[], selectedLineNos: Record<string, boolean>) {
  return groupedQuoteLines.every((group) => {
    const lines = [group.mainQuoteLine, ...group.alternativeQuoteLines];
    return lines.some((line) => selectedLineNos[line.lineNo]);
  });
}

function calculateIsAllDisabled(groupedQuoteLines: GroupedQuoteLines[]) {
  return groupedQuoteLines.every((group) => {
    const lines = [group.mainQuoteLine, ...group.alternativeQuoteLines];
    return lines.every((line) => line.disabled);
  });
}
